import { useEffect } from "react";
import isEmpty from 'lodash.isempty';

// Services & hooks
import { toggleLoading } from "../../services/helpers/index";
import { jobFetchJob } from "../../services/job";
import { paymentService } from '../../services/payment/index';
import { injectIntl, defineMessages } from "react-intl";
import PropTypes from 'prop-types';
import usePayment from "../../hooks/usePayment";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useSessionStorage from "../../hooks/useSessionStorage";
import useUserAccount from "../../hooks/useUserAccount";
import useEnv from "../../hooks/useEnv";

// Strings for translation in this view
const messages = defineMessages({
	paymentVerification: {
		id: "Vahvistetaan maksua",
		defaultMessage: "Vahvistetaan maksua"
	},
	paymentCancelled: {
		id: "Maksaminen keskeytetty",
		defaultMessage: "Maksaminen keskeytetty"
	},
	paymentException: {
		id: "Tapahtui virhe",
		defaultMessage: "Tapahtui virhe"
	},
	paymentSuccess151Or155: {
		id: "Kiitos maksustasi! Pyydämme energiayhtiötäsi peruuttamaan jakelun keskeytyksen. Jos energiayhtiösi on kuitenkin jo ehtinyt aloittaa katkaisuun johtavat toimet ja jakelusi keskeytyy, tulee kaikki avoimet erääntyneet laskut, mahdollinen vakuuslasku sekä katkaisu- ja kytkentälasku olla maksettuna ennen uudelleenkytkentää. Huomioithan, että Ropo ei tee energianjakelun keskeytyksiä, joten yhteydenottosi asiakaspalveluumme ei vaikuta mahdollisiin katkaisutoimiin.",
		defaultMessage: "Kiitos maksustasi! Pyydämme energiayhtiötäsi peruuttamaan jakelun keskeytyksen. Jos energiayhtiösi on kuitenkin jo ehtinyt aloittaa katkaisuun johtavat toimet ja jakelusi keskeytyy, tulee kaikki avoimet erääntyneet laskut, mahdollinen vakuuslasku sekä katkaisu- ja kytkentälasku olla maksettuna ennen uudelleenkytkentää. Huomioithan, että Ropo ei tee energianjakelun keskeytyksiä, joten yhteydenottosi asiakaspalveluumme ei vaikuta mahdollisiin katkaisutoimiin."
	},
	paymentSuccess156: {
		id: "Olemme vastaanottaneet maksuilmoituksesi ja käsittelemme sitä parhaillaan. Tämän jälkeen uudelleenkytkentä siirtyy energiayhtiösi käsittelyyn. Jos takaisinkytkennälle ilmenee esteitä, otamme sinuun välittömästi yhteyttä. Huomioithan, että yhteydenottosi asiakaspalveluumme ei nopeuta asiasi käsittelyä.",
		defaultMessage: "Olemme vastaanottaneet maksuilmoituksesi ja käsittelemme sitä parhaillaan. Tämän jälkeen uudelleenkytkentä siirtyy energiayhtiösi käsittelyyn. Jos takaisinkytkennälle ilmenee esteitä, otamme sinuun välittömästi yhteyttä. Huomioithan, että yhteydenottosi asiakaspalveluumme ei nopeuta asiasi käsittelyä."
	},
	authError: {
		id: 'Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.',
		defaultMessage: 'Tunnistautuminen epäonnistui. Ole hyvä ja yritä uudelleen.'
	}
});

export { messages };

const Payment = (props) => {
	// translations
	const lng = props.intl.formatMessage;
	// router hooks
	const navigate = useNavigate();
	const location = useLocation();
	const { provider, status } = useParams();
	// hooks
	const { checkPaymentStatus } = usePayment();
	const { setSessionItem } = useSessionStorage();
	const { userIsLoggedIn, uuidAuthenticate } = useUserAccount();
	const { isDev } = useEnv();

	const redirectState = JSON.parse(window.localStorage.getItem('paymentRedirectState') ?? '{}');

	/**
	 * Parse query parameters from location. 
	 * @returns {array} - Parsed query parameters as key-value pairs.
	 */
	const parseQueryParams = () => {
		// Throw error location search undefined or null
		if (isEmpty(location?.search)) {
			throw new Error('Location search undefined or null');
		}

		// parse query parameters from location search
		const searchParams = new URLSearchParams(location.search);
		let args = [];

		for (const [key, value] of searchParams.entries()) {
			args[key] = value;
		}

		return args;
	}

	/**
	 * Handle payments from Worldline.
	 * Skips the payment status check from backend, because it is handled in the Worldline callback.
	 * @param {boolean} paymentSuccess
	 */
	const handleWorldlinePayments = async (paymentSuccess) => {
		// form the alert message (success or error messages)
		const alert = paymentSuccess ? formAlertMessage() : formAlertMessage(true);
		paymentService.resetPaymentService();

		// replace with hash routing format to ensure no query parameters remain in the url, which is possible with router version 
		window.history.replaceState(null, '', `${window.location.origin}/#/job`);
		await redirectToJob(alert);
	}

	/**
	 * Helper for forming the alert message content to pass to invoice page.
	 * The message is different, based on the job status before the payment was made.
	 * Returns null when there is no message to show (e.g. a normal successful payment).
	 *
	 * @returns {Object|null} - The alert message object or null.
	 */
	const formAlertMessage = (error) => {
		if (error) {
			return { message: lng(messages.paymentException) + '. ' + lng(messages.paymentCancelled), type: 'danger' };
		}

		// get job's status before it was paid
		const prevStatus = redirectState.jobPrevStatus;

		// show success message based on previous status
		if (prevStatus?.toString() === '151' || prevStatus?.toString() === '155') {
			return { message: lng(messages.paymentSuccess151Or155), type: 'success' };
		} else if (prevStatus?.toString() === '156') {
			return { message: lng(messages.paymentSuccess156), type: 'success' };
		}

		return null;
	}

	/**
	 * Handle payment status check.
	 * Form the appropriate alert message and handle redirection to job view.
	 *
	 * @param {string} transactionId
	 * @param {string} serviceParam
	 */
	const handleStatusCheck = async (transactionId, serviceParam) => {
		const { error } = await checkPaymentStatus(transactionId, serviceParam);
		const alert = error ? formAlertMessage(error) : formAlertMessage();
		paymentService.resetPaymentService();
		redirectToJob(alert);
	};

	useEffect(() => {
		const signIn = async (state) => {
			if (isEmpty(state) || isEmpty(redirectState.state)) {
				if (isDev) {
					console.log("State is missing, cannot authenticate");
				}
				return
			}
			// UUID is formed by concatenating public and local parts
			const uuid = state + redirectState.state;
			if (isDev) {
				console.log("Trying to sign in with uuid: ", uuid);
			}
			try {
				await uuidAuthenticate(uuid);
			} catch (error) {
				if (isDev) {
					console.error("User authentication failed after payment, error: ", error);
				}
			}
		}

		const handleRedirect = async () => {
			try {
				// set payment provider to session storage when applicable to ensure callback parameters are checked correctly
				if (Object.values(paymentService.PAYMENTMETHODS).includes(provider)) {
					setSessionItem('paymentService', provider);
				}
				// parse args from query string
				const args = parseQueryParams();

				// If user is not authenticated, try to sign in. This might be because of redirect to a new browser tab.
				if (!userIsLoggedIn()) {
					await signIn(args.state);
				}

				const transactionId = paymentService.getCallbackTransactionId(args);
				const paymentSuccess = paymentService.getCallbackSuccess(args, status);

				// loading spinner and message
				if (paymentSuccess) {
					// Message to user that transaction is verifying
					toggleLoading(true, lng(messages.paymentVerification) + " (" + transactionId + ")...");
				} else {
					// Message to user that transaction is cancelled (or failed for some reason)
					toggleLoading(true, lng(messages.paymentCancelled) + "...");
				}

				/**
				 * If paymentservice is Worldline, redirect to job and skip the payment status call,
				 * because it is handled in its own callback.
				 */
				if (provider === paymentService.PAYMENTMETHODS.WORLDLINE) {
					await handleWorldlinePayments(paymentSuccess);
					return;
				}

				const storageItem = redirectState.service;
				let serviceParam = '';

				// check if service should be added to query parameters
				if (Object.values(paymentService.PAYMENTMETHODS).indexOf(storageItem) > -1) {
					serviceParam = '?service=' + storageItem;
				}

				// check payment status from backend
				await handleStatusCheck(transactionId, serviceParam);
			} catch (error) {
				// Show generic error message
				paymentService.resetPaymentService();
				await redirectToJob(formAlertMessage(error));
			} finally {
				window.localStorage.removeItem('paymentRedirectState');
			}
		}
		void handleRedirect();
	}, []);

	/**
	 * Reload job and redirect
	 */
	const redirectToJob = async (alert) => {
		const dpid = redirectState.jobDpid;

		if (isEmpty(dpid)) {
			// If dpid is missing, go to job search page with error message, because we cannot know which job to show
			navigate("/jobsearch", { state: { alert }, replace: true });
			return;
		}

		try {
			await jobFetchJob(dpid);
			toggleLoading(false);
			navigate("/job", {state: {alert}, replace: true});
		} catch (error) {
			if (error.response?.status === 401) {
				const alert = { message: lng(messages.authError), type: 'danger' };
				navigate("/logout", { state: { alert }, replace: true  });
			} else {
				// If fetchjob fails -> cannot refresh job
				// go still back, there is job with status before payment
				alert = { message: lng(messages.paymentException) + '. ' + lng(messages.paymentCancelled), type: 'danger' };
				navigate("/job", { state: { alert }, replace: true });
			}
		} finally {
			toggleLoading(false);
		}
	}

	return (<div></div>);
};

Payment.propTypes = {
	intl: PropTypes.object.isRequired
};

export default injectIntl(Payment);